/**
 * The one place that talks to the API.
 *
 * Two things it does that callers rely on:
 *   - a 401 triggers exactly one refresh, and every request that arrived
 *     during that window waits on the same promise rather than each
 *     firing its own refresh (which would rotate the family and log the
 *     admin out, the classic refresh stampede);
 *   - errors keep their `code` and `details`, because several screens
 *     branch on them — CONFIRM_REMOVAL opens a dialog, POSSIBLE_DUPLICATE
 *     opens another, VALIDATION_ERROR paints individual fields red.
 */

const BASE = (import.meta.env.VITE_API_URL ?? '') + '/api/v1';

const ACCESS_KEY = 'stz.access';
const REFRESH_KEY = 'stz.refresh';

export class ApiClientError extends Error {
  status: number;
  code: string;
  details: Record<string, unknown> | undefined;
  fields: Record<string, string> | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>,
    fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.fields = fields;
  }
}

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

type Listener = () => void;
const signOutListeners = new Set<Listener>();

/** The auth provider subscribes so an expired session redirects to login. */
export function onSignOut(fn: Listener): () => void {
  signOutListeners.add(fn);
  return () => { signOutListeners.delete(fn); };
}

function forceSignOut() {
  tokenStore.clear();
  signOutListeners.forEach((fn) => fn());
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = tokenStore.refresh;
  if (!refreshToken) return false;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Client': 'web-admin' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const body = await res.json();
      const tokens = body?.data?.tokens;
      if (!tokens?.accessToken) return false;
      tokenStore.set(tokens.accessToken, tokens.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so callers awaiting this promise all
      // see the same result before a new refresh can start.
      setTimeout(() => { refreshInFlight = null; }, 0);
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Set false for login/refresh, which must not retry on 401. */
  auth?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, auth = true, signal } = options;

  let url = BASE + path;
  if (query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { 'X-Client': 'web-admin' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const access = tokenStore.access;
    if (auth && access) headers.Authorization = `Bearer ${access}`;

    return fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  };

  let res = await send();

  if (res.status === 401 && auth && tokenStore.refresh) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      res = await send();
    } else {
      forceSignOut();
    }
  }

  if (res.status === 204) return undefined as T;

  let payload: {
    ok?: boolean;
    data?: T;
    error?: {
      code?: string;
      message?: string;
      details?: Record<string, unknown>;
      fields?: Record<string, string>;
    };
  };

  try {
    payload = await res.json();
  } catch {
    throw new ApiClientError(
      res.status,
      'BAD_RESPONSE',
      res.ok ? 'The server sent a response we could not read.' : `Request failed (${res.status}).`,
    );
  }

  if (!res.ok || payload.ok === false) {
    if (res.status === 401) forceSignOut();
    const error = payload.error ?? {};
    throw new ApiClientError(
      res.status,
      error.code ?? 'UNKNOWN',
      error.message ?? 'Something went wrong.',
      error.details,
      error.fields,
    );
  }

  return payload.data as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
  /** Login must not attempt a refresh when it 401s. */
  anon: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body, auth: false }),
};
